/*
 * <video-reviews> — playback controller for the video review carousel.
 *
 * Wraps Dawn's <slider-component>, which keeps owning scroll, swipe and the
 * prev/next buttons. This element only handles play/pause: one clip at a time,
 * the control's icon and aria-label reflecting the actual media state.
 *
 * Every query is scoped to `this`, never `document` — two instances on one
 * page stay independent, and a Section Rendering API rerender constructs a
 * fresh element rather than leaving stale listeners behind.
 *
 * State lives on the media element, not in a field: `play` / `pause` / `ended`
 * fire for keyboard, PiP and OS media keys too, so driving the UI off those
 * events keeps the icon honest no matter what started playback.
 */
class VideoReviews extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-video-review]'));

    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      const control = card.querySelector('[data-video-review-control]');
      if (!video || !control) return;

      control.addEventListener('click', () => {
        if (video.paused) {
          this.play(video);
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', () => {
        this.pauseOthers(video);
        this.setState(card, control, true);
      });
      video.addEventListener('pause', () => this.setState(card, control, false));
      video.addEventListener('ended', () => this.setState(card, control, false));
    });
  }

  play(video) {
    // A rejected play() (autoplay policy, a decode error) must not leave the
    // card showing a pause icon — the `play` event never fires, so the UI is
    // already correct; swallow the rejection so it doesn't surface as an
    // unhandled promise.
    const played = video.play();
    if (played) played.catch(() => {});
  }

  pauseOthers(current) {
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (video && video !== current) video.pause();
    });
  }

  setState(card, control, isPlaying) {
    card.toggleAttribute('data-playing', isPlaying);
    const label = isPlaying ? control.dataset.pauseLabel : control.dataset.playLabel;
    if (label) control.setAttribute('aria-label', label);
  }
}

if (!customElements.get('video-reviews')) {
  customElements.define('video-reviews', VideoReviews);
}
